// src/services/adminSettingsService.ts

import { apiClient } from "./apiClient";

// ── Setting key constants (mirror of SettingConstants.java) ───────────────────
export const SETTING_KEYS = {
    // General
    SITE_NAME:                    "site.name",
    SITE_SLOGAN:                  "site.slogan",
    SITE_MAINTENANCE:             "site.maintenance.enabled",
    SITE_ANNOUNCEMENT_ENABLED:    "site.announcement.enabled",
    SITE_ANNOUNCEMENT_TEXT:       "site.announcement.text",

    // Registration & User
    REGISTRATION_ENABLED:         "registration.enabled",
    DEFAULT_ROLE:                 "registration.default_role",
    USER_BIO_MAX_LENGTH:          "user.bio.max_length",
    // Username max length (hard ceiling 50 — snapshot columns are VARCHAR(50))
    USERNAME_MAX_LENGTH:          "user.username.max_length",
    EMAIL_VERIFICATION:           "registration.email_verification",

    // Authentication tokens — validity windows of the mailed one-time links and
    // the anti-abuse cooldown on the "resend verification mail" endpoint
    AUTH_EMAIL_VERIFICATION_TOKEN_HOURS:       "auth.email_verification_token_hours",
    AUTH_PASSWORD_RESET_TOKEN_HOURS:           "auth.password_reset_token_hours",
    AUTH_VERIFICATION_RESEND_COOLDOWN_MINUTES: "auth.verification_resend_cooldown_minutes",

    // Entry Rules
    ENTRY_MIN_LENGTH:             "entry.min_length",
    ENTRY_MAX_LENGTH:             "entry.max_length",
    ENTRY_DAILY_LIMIT:            "entry.daily_limit",
    ENTRY_EDIT_WINDOW_MINUTES:    "entry.edit_window_minutes",
    // Maximum edit actions per user per day; 0 = unlimited
    ENTRY_DAILY_EDIT_LIMIT:       "entry.daily_edit_limit",
    ENTRY_DRAFT_LIMIT:            "entry.draft_limit",
    // Lines shown before "show more" appears; 0 = no truncation
    ENTRY_PREVIEW_LINE_LIMIT:     "entry.preview_line_limit",
    // Custom topic title max length (allowed range 50-80, VARCHAR(80) columns)
    TOPIC_TITLE_MAX_LENGTH:       "topic.title.max_length",
    // Rate limits for user topic suggestions ("başlık öner")
    TOPIC_SUGGESTION_COOLDOWN_SECONDS: "topic.suggestion.cooldown_seconds",
    TOPIC_SUGGESTION_DAILY_LIMIT:      "topic.suggestion.daily_limit",

    // Moderation
    MOD_AUTO_BAN_THRESHOLD:       "moderation.auto_ban_report_threshold",
    MOD_REPORT_COOLDOWN_DAYS:     "moderation.report_cooldown_days",
    MOD_SPAM_COOLDOWN_SECONDS:    "moderation.spam_cooldown_seconds",
    MOD_DEFAULT_IP_BAN_DAYS:      "moderation.default_ip_ban_days",

    // Messaging
    MSG_MAX_LENGTH:               "messaging.max_length",
    MSG_INBOX_LIMIT:              "messaging.inbox_limit",
    MSG_RATE_LIMIT_MAX_MESSAGES:  "messaging.rate_limit_max_messages",
    MSG_RATE_LIMIT_WINDOW_SECONDS:"messaging.rate_limit_window_seconds",

    // Search
    SEARCH_TYPEAHEAD_MIN_CHARS:   "search.typeahead_min_chars",
    SEARCH_TYPEAHEAD_DELAY_MS:    "search.typeahead_delay_ms",
    SEARCH_RESULT_LIMIT:          "search.result_limit",

    // Media upload limits (values in MB)
    MEDIA_AVATAR_MAX_MB:          "media.avatar_max_mb",
    MEDIA_BANNER_MAX_MB:          "media.banner_max_mb",
    MEDIA_ENTRY_IMAGE_MAX_MB:     "media.entry_image_max_mb",
} as const;

export type SettingKey = typeof SETTING_KEYS[keyof typeof SETTING_KEYS];

// ── DTO shape returned by the API ─────────────────────────────────────────────
export interface SystemSettingDto {
    key:                string;
    value:              string;
    description:        string;
    updatedAt:          string | null;
    updatedByUsername:  string | null;
}

// ── API helpers ───────────────────────────────────────────────────────────────

/**
 * Fetch all system settings.
 * Returns a flat array; the component groups them by key prefix.
 */
export const getAllSettings = (): Promise<SystemSettingDto[]> =>
    apiClient.get<SystemSettingDto[]>("/api/admin/settings");

/**
 * Batch-update a group of settings.
 * @param settings  plain object mapping setting key → new string value
 */
export const batchUpdateSettings = (
    settings: Record<string, string>
): Promise<SystemSettingDto[]> =>
    apiClient.put<SystemSettingDto[]>("/api/admin/settings/batch", { settings });

// ── Convenience ───────────────────────────────────────────────────────────────

/**
 * Convert the flat settings array to a key → value lookup map.
 * Used by the component to initialise local form state.
 */
export const toValueMap = (settings: SystemSettingDto[]): Record<string, string> =>
    settings.reduce<Record<string, string>>((acc, s) => {
        acc[s.key] = s.value;
        return acc;
    }, {});